// src/pages/FindOncologistsStatePage.jsx
import React, { useState, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  ShieldCheck, 
  MapPin, 
  Phone, 
  Stethoscope, 
  ArrowRight, 
  CheckCircle2, 
  Building2, 
  ChevronRight,
  Filter,
  Search,
  ExternalLink,
  Users
} from 'lucide-react';
import MetaTags from '@/components/SEO/MetaTags';
import { SITE_ORIGIN } from '@/config/site';
import { US_STATES } from '@/data/usStatesData';
import { 
  ONCOLOGY_TAXONOMIES, 
  QUALIFIED_CITIES, 
  ONCOLOGISTS_BY_STATE 
} from '@/data/oncologistsData';
import Footer from '@/components/Footer';

export default function FindOncologistsStatePage() {
  const { stateSlug } = useParams();
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const stateInfo = US_STATES.find((s) => s.slug === stateSlug);
  const providers = ONCOLOGISTS_BY_STATE[stateSlug] || [];

  const filteredProviders = useMemo(() => {
    return providers.filter((p) => {
      if (selectedSpecialty !== 'all') {
        const specLower = p.specialty.toLowerCase();
        if (selectedSpecialty === 'medical' && !specLower.includes('medical')) return false;
        if (selectedSpecialty === 'radiation' && !specLower.includes('radiation')) return false;
        if (selectedSpecialty === 'surgical' && !specLower.includes('surgical')) return false;
        if (selectedSpecialty === 'hematology' && !specLower.includes('hematology')) return false;
        if (selectedSpecialty === 'gynecologic' && !specLower.includes('gynecolog')) return false;
        if (selectedSpecialty === 'pediatric' && !specLower.includes('pediatric')) return false;
      }
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        p.name.toLowerCase().includes(q) ||
        p.city.toLowerCase().includes(q) ||
        p.specialty.toLowerCase().includes(q) ||
        p.affiliation.toLowerCase().includes(q)
      );
    });
  }, [providers, selectedSpecialty, searchTerm]);

  if (!stateInfo) {
    return <Navigate to="/find-oncologists" replace />;
  }

  const stateQualifiedCities = QUALIFIED_CITIES.filter((c) => c.stateSlug === stateSlug);
  const pageUrl = `${SITE_ORIGIN}/find-oncologists/${stateSlug}`;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: `Best Oncologists in ${stateInfo.name} (${stateInfo.abbr}) | NPI Registry Directory`,
      description: `Verified directory of board-certified oncologists in ${stateInfo.name}. Practice addresses, phone numbers, and NPI credentials for medical, radiation, and surgical oncologists.`,
      url: pageUrl,
      about: {
        '@type': 'MedicalSpecialty',
        name: 'Oncology',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: 'Find Oncologists', item: `${SITE_ORIGIN}/find-oncologists` },
        { '@type': 'ListItem', position: 3, name: stateInfo.name, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: filteredProviders.slice(0, 15).map((p, idx) => ({
        '@type': 'Physician',
        position: idx + 1,
        name: p.name,
        medicalSpecialty: p.specialty,
        telephone: p.phone,
        address: {
          '@type': 'PostalAddress',
          streetAddress: p.practiceAddress,
          addressLocality: p.city,
          addressRegion: p.state,
          postalCode: p.postalCode,
          addressCountry: 'US',
        },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal font-sans antialiased">
      <MetaTags
        title={`Best Oncologists in ${stateInfo.name} (${stateInfo.abbr}) | Verified NPI Directory | ByOnco`}
        description={`Find verified cancer doctors and oncologists in ${stateInfo.name}. Practice addresses, direct phone numbers, and taxonomy credentials for top medical, surgical, and radiation oncologists.`}
        path={`/find-oncologists/${stateSlug}`}
        jsonLd={jsonLd}
      />

      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Breadcrumb" className="border-b border-border-mist bg-white/60 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between text-xs sm:text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <Link to="/" className="text-gray-500 hover:text-forest-ink transition">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/find-oncologists" className="text-gray-500 hover:text-forest-ink transition">
                Find Oncologists
              </Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-forest-ink font-semibold" aria-current="page">{stateInfo.name}</li>
          </ol>
          <div className="flex items-center gap-3">
            <Link 
              to={`/cancer-centers/${stateSlug}`}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-emerald-800 hover:text-forest-ink font-semibold"
            >
              {stateInfo.abbr} Cancer Centers Directory <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link 
              to="/cancer-second-opinion" 
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-forest-ink text-white text-xs font-medium hover:bg-forest-shadow transition"
            >
              Get Second Opinion <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Header */}
      <header className="relative pt-10 pb-12 bg-gradient-to-b from-emerald-50/70 via-cream-paper to-cream-paper border-b border-border-mist">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-100 text-emerald-900 text-xs sm:text-sm font-medium mb-4">
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            CMS NPPES Registry Verified · {stateInfo.name} Oncology Directory
          </div>

          <h1 className="text-3xl sm:text-5xl font-bold font-serif text-forest-ink tracking-tight mb-3">
            Best Oncologists in {stateInfo.name} ({stateInfo.abbr})
          </h1>
          <p className="max-w-3xl mx-auto text-base sm:text-lg text-charcoal/85 leading-relaxed mb-6">
            Explore board-certified medical, surgical, and radiation oncologists practicing across {stateInfo.name}. Verified practice addresses, telephone contacts, and NPI identifiers.
          </p>

          {/* Quick Metrics */}
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs sm:text-sm text-charcoal/80">
            <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-border-mist shadow-xs">
              <Users className="w-4 h-4 text-emerald-700" /> {providers.length}+ Verified Oncologists
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-border-mist shadow-xs">
              <Building2 className="w-4 h-4 text-emerald-700" /> {stateInfo.nciCount} NCI-Designated Center{stateInfo.nciCount !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-border-mist shadow-xs">
              <Stethoscope className="w-4 h-4 text-emerald-700" /> 6 Oncology Subspecialties
            </span>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-12">

        {/* City Spoke Links (If 10+ providers) */}
        {stateQualifiedCities.length > 0 && (
          <section className="bg-white p-6 rounded-2xl border border-border-mist shadow-xs">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 pb-3 border-b border-border-mist">
              <div>
                <h2 className="text-lg font-bold font-serif text-forest-ink">
                  {stateInfo.name} High-Density Metro Oncology Directories
                </h2>
                <p className="text-xs text-charcoal/70">
                  Select a major city to view localized practice addresses and clinical phone contacts.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {stateQualifiedCities.map((c) => (
                <Link
                  key={c.citySlug}
                  to={c.path}
                  className="p-3.5 rounded-xl border border-border-mist hover:border-emerald-600 hover:bg-emerald-50/40 transition flex items-center justify-between group"
                >
                  <div>
                    <h3 className="font-bold text-forest-ink text-sm group-hover:text-emerald-800 transition">
                      {c.cityName} Oncologists
                    </h3>
                    <p className="text-xs text-gray-500">{c.providerCount} verified physicians</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-700 group-hover:translate-x-0.5 transition" />
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Filter & Search Bar */}
        <section className="bg-white p-5 rounded-2xl border border-border-mist shadow-xs space-y-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder={`Search ${stateInfo.name} oncologists by name, city, or hospital...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:border-forest-ink text-sm text-charcoal"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedSpecialty('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'all'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All Specialties
              </button>
              <button
                onClick={() => setSelectedSpecialty('medical')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'medical'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Medical Onc
              </button>
              <button
                onClick={() => setSelectedSpecialty('radiation')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'radiation'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Radiation Onc
              </button>
              <button
                onClick={() => setSelectedSpecialty('surgical')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'surgical'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Surgical Onc
              </button>
              <button
                onClick={() => setSelectedSpecialty('hematology')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  selectedSpecialty === 'hematology'
                    ? 'bg-forest-ink text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Hem-Onc
              </button>
            </div>
          </div>
        </section>

        {/* Provider Cards Directory */}
        <section className="space-y-4">
          <div className="flex items-center justify-between text-xs sm:text-sm text-charcoal/80 mb-2">
            <span>Showing <strong>{filteredProviders.length}</strong> verified oncology providers in {stateInfo.name}</span>
            <span className="text-gray-500">Source: CMS NPPES NPI Registry</span>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {filteredProviders.map((provider) => (
              <div
                key={provider.npi}
                className="p-5 rounded-2xl bg-white border border-border-mist hover:border-emerald-300 transition shadow-xs flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-bold text-forest-ink text-base">
                        {provider.name}
                      </h3>
                      <p className="text-xs text-emerald-800 font-semibold flex items-center gap-1.5 mt-0.5">
                        <Stethoscope className="w-3.5 h-3.5" /> {provider.specialty}
                      </p>
                    </div>
                    <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-gray-100 text-gray-600 flex-shrink-0">
                      NPI: {provider.npi}
                    </span>
                  </div>

                  <p className="text-xs text-charcoal/70 mb-3 font-medium flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{provider.affiliation}</span>
                  </p>

                  <div className="space-y-1 text-xs text-charcoal/80 bg-cream-paper/50 p-3 rounded-lg border border-border-mist">
                    <div className="flex items-start gap-2">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span>{provider.practiceAddress}, {provider.city}, {provider.state} {provider.postalCode}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-1 border-t border-border-mist/60">
                      <Phone className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                      <a href={`tel:${provider.phone}`} className="font-semibold text-emerald-800 hover:underline">
                        {provider.phone}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-border-mist flex items-center justify-between text-xs">
                  <span className="text-gray-500 font-mono text-[11px]">
                    Taxonomy: {provider.taxonomyCode}
                  </span>
                  <Link
                    to="/cancer-second-opinion"
                    className="inline-flex items-center gap-1 text-emerald-800 hover:text-forest-ink font-semibold"
                  >
                    Online Second Opinion <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {filteredProviders.length === 0 && (
            <div className="p-8 rounded-2xl bg-white border border-border-mist text-center text-charcoal/70">
              <p className="font-semibold mb-1">No providers found matching your filter criteria.</p>
              <p className="text-xs">Try selecting "All Specialties" or clearing your search keywords.</p>
            </div>
          )}
        </section>

        {/* Cross Link to State Cancer Centers Directory */}
        <section className="bg-gradient-to-r from-forest-ink to-emerald-950 text-white rounded-3xl p-8 text-center shadow-lg">
          <h2 className="text-xl sm:text-3xl font-bold font-serif text-white mb-2">
            Looking for Hospital-Based Cancer Centers in {stateInfo.name}?
          </h2>
          <p className="text-emerald-100/90 text-xs sm:text-sm max-w-2xl mx-auto mb-6">
            View our comprehensive guide to NCI-designated comprehensive cancer centers, proton therapy facilities, and hospital networks in {stateInfo.name}.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to={`/cancer-centers/${stateSlug}`}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-forest-ink font-semibold text-xs sm:text-sm hover:bg-emerald-50 transition shadow-sm"
            >
              Explore {stateInfo.name} Cancer Centers <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/find-oncologists"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-white/30 text-white font-medium text-xs sm:text-sm hover:border-white transition"
            >
              View All 50 States
            </Link>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
